import React, { useMemo } from 'react';
import { Box, Tab, Tabs, Typography } from '@mui/material';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import { apiClient } from '../../services/api';

const WEB_ACTIVITY_PATH = '/workforce-monitoring/web-activity';

const BASE_TABS = [
  { label: 'Overview', path: '/workforce-monitoring/overview' },
  { label: 'Workforce', path: '/workforce-monitoring/workforce' },
  { label: 'Computers', path: '/workforce-monitoring/computers' },
  { label: 'Workforce View', path: '/workforce-monitoring/live-wall', permission: 'monitoring.live_wall.read' },
  { label: 'Activity', path: '/workforce-monitoring/activity' },
  { label: 'Applications', path: '/workforce-monitoring/applications' },
  { label: 'Web Activity', path: '/workforce-monitoring/web-activity' },
  { label: 'Sessions', path: '/workforce-monitoring/sessions' },
  { label: 'Alerts', path: '/workforce-monitoring/alerts' },
  { label: 'Reports', path: '/workforce-monitoring/reports' },
  { label: 'Configuration', path: '/workforce-monitoring/configuration' },
];

const WorkforceMonitoringLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const permissions = user?.permissions || [];
  const canReadConsole = permissions.includes('monitoring.console.read')
    || user?.role === 'super_user'
    || user?.role === 'administrator'
    || user?.role === 'manager'
    || user?.role === 'supervisor';

  // Structured Web Activity is release-gated (deferred pending supported managed-browser
  // deployment). Hide the tab unless the organization has enabled the feature.
  const { data: configData } = useQuery({
    queryKey: ['monitoring', 'configuration', 'nav'],
    queryFn: () => apiClient.monitoring.getConfiguration(),
    staleTime: 60_000,
  });
  const webActivityEnabled = Boolean((configData as any)?.data?.data?.web_activity_feature_enabled);

  const tabs = useMemo(
    () => BASE_TABS.filter((tab) =>
      (!tab.permission || permissions.includes(tab.permission))
      && (tab.path !== WEB_ACTIVITY_PATH || webActivityEnabled)),
    [permissions, webActivityEnabled],
  );

  if (!canReadConsole) {
    return (
      <Box sx={{ mt: 2 }}>
        <Typography color="error">You do not have permission to view Workforce Monitoring.</Typography>
      </Box>
    );
  }

  const currentTab = tabs.find((tab) => location.pathname.startsWith(tab.path))?.path
    || (location.pathname.includes('/computers/') ? '/workforce-monitoring/computers' : '/workforce-monitoring/overview');

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h4" gutterBottom>Workforce Monitoring</Typography>
        <Typography variant="body2" color="text.secondary">
          Operational console for employees, computers, activity, and agent health.
        </Typography>
      </Box>
      <Tabs
        value={currentTab}
        onChange={(_, value) => navigate(value)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        {tabs.map((tab) => (
          <Tab key={tab.path} label={tab.label} value={tab.path} />
        ))}
      </Tabs>
      <Outlet />
    </Box>
  );
};

export default WorkforceMonitoringLayout;
